/**
 * H-FE-1 — the handover screens' words and readings, once.
 *
 * Copy is VERBATIM from the approved prototype
 * (`docs/design/mockups/prototipo-handover-v2.html`); every value read here is
 * what the server serves (`GET /handover-inspections/{id}`, `GET /punch-items`,
 * the asset QA table's `booking` facts). Nothing here decides a rule the server
 * decides: the pages draw what these return.
 */
import { fshort, fstamp, names, toDate } from './ncrView';
import { fmtShortDay } from './utils';

export { fshort, fstamp, names, toDate };

/** "Wed 24 Sep 2026, 08:00" — the prototype's `fAt`; DATES-1: the app's one
 *  instant (`fmtShortDay`), with the year. */
export const fAt = (value) => (toDate(value) ? fmtShortDay(value, { time: true }) : '');

// ---------------------------------------------------------------------------
// FIX-FE-1 — ONE CLOCK ON THE HANDOVER SCREENS: THE PROJECT'S, AS SERVED
// ---------------------------------------------------------------------------
//
// `tz` is the `project_timezone` the read served (the inspection and the list:
// at the top; the asset QA table: on each row). With it, a day reads
// "Mon 5 Oct" and an instant "Mon 5 Oct, 09:00" on that clock (`fmtShortDay`).
// WITHOUT it (an answer from before CLEAN-BE-2, a read that failed) the screen
// reads exactly as it did — no zone is ever made up here.

/** A calendar date (`YYYY-MM-DD`) is a date, never an instant: no zone moves it. */
const isCalendarDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const onProjectClock = (value, tz) => typeof tz === 'string' && !!tz.trim() && !isCalendarDate(value);

/** "Mon 5 Oct" on the project's clock; without `tz`, as before. */
export const hoDay = (value, tz) => (onProjectClock(value, tz) ? fmtShortDay(value, tz) : fshort(value));

/** "Mon 5 Oct, 09:00" on the project's clock; without `tz`, as before. */
export const hoStamp = (value, tz) => (onProjectClock(value, tz)
  ? fmtShortDay(value, tz, { time: true }) : fstamp(value));

/** The same instant inside a sentence ("Completed on …"); without `tz`, `fAt`. */
export const hoAt = (value, tz) => (onProjectClock(value, tz)
  ? fmtShortDay(value, tz, { time: true }) : fAt(value));

/**
 * FIX-FE-2 (B2) — the calendar day (`YYYY-MM-DD`) an instant falls on, for a
 * sheet: on the project's clock with `tz`; without it (or with a zone the
 * runtime does not know) the day written in the ISO string, as before.
 */
export const hoIsoDay = (value, tz) => {
  if (!value) return '';
  const asBefore = String(value).split('T')[0];
  if (!onProjectClock(value, tz)) return asBefore;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return asBefore;
  try {
    const parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz.trim(), year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    return asBefore;
  }
};

// ---------------------------------------------------------------------------
// Categories (prompt H-FE-1, verbatim)
// ---------------------------------------------------------------------------

export const CAT_NOTE = 'Every punch item gets fixed. It stays Open in the register until the fix is approved.';

export const CAT_TITLE = { A: 'Blocks handover', B: 'Does not block handover', C: 'Minor' };

/** The rule line under each category. On a QA Closeout, A says "before QA Complete". */
export const catRule = (cat, qaCloseout = false) => {
  if (cat === 'A') {
    return qaCloseout
      ? 'Fixed and approved before QA Complete. Open until then.'
      : 'Fixed and approved before handover. Open until then.';
  }
  if (cat === 'B') return 'Fixed and approved after handover. Must be closed before QA Complete. Open until then.';
  return 'Fixed and approved after handover. Does not hold up QA Complete. Open until then.';
};

export const CATEGORIES = ['A', 'B', 'C'];

// ---------------------------------------------------------------------------
// Punch item states: server `open | closed | verified`
// ---------------------------------------------------------------------------

export const PUNCH_STATE_LABEL = {
  open: 'Open',
  closed: 'Rectified — awaiting approval',
  verified: 'Closed',
};

export const punchStateLabel = (status) => PUNCH_STATE_LABEL[status] || status || '';

/** "Open" in the register means NOT YET APPROVED (prototype: open until the fix is approved). */
export const isPunchOpen = (item) => item?.status !== 'verified';

/** "Ⓐ 1 open · Ⓒ 1 open" — the counts per category of the items still open. */
export const openByCategory = (items = []) => {
  const out = { A: 0, B: 0, C: 0 };
  (items || []).filter(isPunchOpen).forEach((i) => {
    if (i?.category in out) out[i.category] += 1;
  });
  return CATEGORIES.filter((c) => out[c]).map((c) => ({ cat: c, n: out[c] }));
};

export const lateLabel = (item, long = false) => {
  if (item?.raised_after_qa_complete) return long ? 'Raised after QA Complete' : 'After QA Complete';
  if (item?.raised_after_inspection_completion) return long ? 'Raised after the inspection' : 'After the inspection';
  return '';
};

export const UNASSIGNED = 'To be assigned';

// ---------------------------------------------------------------------------
// Handover inspection
// ---------------------------------------------------------------------------

export const isQaCloseout = (hi) => hi?.kind === 'qa_closeout';

export const inspectionKindTitle = (hi) => (isQaCloseout(hi) ? 'QA Closeout Inspection' : 'Handover Inspection');

/** The instant of the inspection: `scheduled_at` when served, else the date and time strings. */
export const scheduledInstant = (hi) => {
  if (hi?.scheduled_at) return toDate(hi.scheduled_at);
  if (!hi?.scheduled_for) return null;
  const day = String(hi.scheduled_for).split('T')[0];
  const time = /^\d{1,2}:\d{2}/.test(hi?.scheduled_time || '') ? hi.scheduled_time : '00:00';
  return toDate(`${day}T${time.length === 4 ? `0${time}` : time}:00`);
};

export const inspectionStatusLabel = (hi, now = new Date()) => {
  if (hi?.status === 'cancelled') return 'Cancelled';
  if (hi?.status === 'completed') return 'Completed';
  const at = scheduledInstant(hi);
  return at && now >= at ? 'In progress' : 'Scheduled';
};

/**
 * FIX-FE-1 — when the inspection is: the served instant (`scheduled_at`) on the
 * project's clock. Without the instant or without `tz`, as before — the date
 * and time strings are a wall clock, and a zone must not move them.
 *
 * DATES-1 — with the year. The instant without `tz` is `TIMEZONE`'s
 * (`fstamp`); the date and time strings are printed as written — the day read
 * as a calendar date, the hour as the string says it.
 */
const wallClockText = (hi) => {
  if (!hi?.scheduled_for) return '';
  const day = fmtShortDay(String(hi.scheduled_for).split('T')[0]);
  const t = /^(\d{1,2}):(\d{2})/.exec(hi?.scheduled_time || '');
  return day ? `${day}, ${t ? `${t[1].padStart(2, '0')}:${t[2]}` : '00:00'}` : '';
};

export const scheduledText = (hi, tz) => {
  if (hi?.scheduled_at && onProjectClock(hi.scheduled_at, tz)) return fmtShortDay(hi.scheduled_at, tz, { time: true });
  return hi?.scheduled_at ? fstamp(hi.scheduled_at) : wallClockText(hi);
};

/**
 * GRID-FE-2 — the Assets grid's Handover cell, over the row's
 * `handover_inspection` (`{id, reference, status, scheduled_at, completed_at}`,
 * GRID-BE-1c): "LSF-HI-002 · held", "LSF-HI-003 · booked 2026-10-06"; '' without
 * one. `fmt` is the caller's date formatter — the QA table has no project clock.
 */
export const handoverCellText = (hi, fmt) => {
  if (!hi) return '';
  const day = (v) => (typeof fmt === 'function' ? fmt(v) : v);
  let state;
  if (hi.status === 'completed') state = 'held';
  else if (hi.status === 'scheduled') state = hi.scheduled_at ? `booked ${day(hi.scheduled_at)}` : 'booked';
  else state = hi.status;
  return [hi.reference, state].filter(Boolean).join(' · ');
};

/** The company whose signature completes the inspection (F1): the receiver, or the EP on a closeout. */
export const receivingCompanyName = (hi) => (isQaCloseout(hi)
  ? hi?.handover_from_project_company_name
  : hi?.handover_to_project_company_name) || '';

export const CLASSIFICATION_LABEL = {
  executing_party: 'Executing Party',
  observer: 'Observer',
};

/** An attendee that still owes an answer: invited or present-and-unsigned, never external. */
export const isUnresolved = (a) => a?.kind !== 'external'
  && (a?.state === 'invited' || a?.state === 'awaiting_signature');

/**
 * The line under the inspection's lifecycle (prototype `hiLife`), with the
 * one sentence the server adds: a completion REQUESTED and still waiting.
 */
export const hiLifeNote = (hi, companyOf, now = new Date()) => {
  if (hi?.status === 'cancelled') return 'Cancelled.';
  if (hi?.status === 'completed') return 'Handover Completed and QA Complete follow per asset (see the assets table).';
  const at = scheduledInstant(hi);
  const attendees = hi?.attendees || [];
  const requested = hi?.completion?.requested;
  const tz = hi?.project_timezone;
  if (at && now < at && !requested) {
    return `Booked for ${scheduledText(hi, tz)}.`;
  }
  const signed = attendees.filter((a) => a.signed_at);
  const notYet = attendees.filter(isUnresolved);
  const byCo = {};
  notYet.forEach((a) => {
    const co = companyOf(a) || 'no company';
    (byCo[co] = byCo[co] || []).push(a.name);
  });
  const waitTxt = Object.keys(byCo).map((c) => `${byCo[c].join(', ')} (${c})`).join(' and ');
  const rc = receivingCompanyName(hi);
  const blockers = hi?.completion?.blockers || [];
  const receiverSigned = !blockers.some((b) => b.code === 'no_receiving_attendee_signed'
    || b.code === 'no_executing_party_attendee_signed');
  let tail;
  if (notYet.length) tail = `Not signed yet: ${waitTxt}.`;
  else tail = receiverSigned ? 'Next: the organiser completes the inspection.' : `Someone from ${rc} must sign.`;
  const lead = signed.length ? `Signed: ${signed.map((a) => a.name).join(', ')}. ` : '';
  const req = requested ? ' Completion requested: it completes once every present attendee is resolved and someone from '
    + `${rc} has signed.` : '';
  return `${lead}${tail}${req}`;
};

/** "5 working days" / "1 working day". */
export const workingDaysText = (n) => {
  const days = Number(n) || 0;
  return `${days} working day${days === 1 ? '' : 's'}`;
};

/**
 * H-FE-2 B3 — "Waiting for <company> to sign — <days> days", from the served
 * `waiting_for_signature {company_name, days}` (a label, never a gate); '' when null.
 *
 * H-FE-3 (§19) — with the served `limit {days, passes_at, passed}`: "· time limit
 * 5 working days, passes Mon 6 Oct, 09:00" while it runs, "· time limit passed Mon 6 Oct"
 * once it has passed. No `limit` ("No time limit"): as before.
 *
 * CLEAN-FE-1 — while it runs, the instant it passes carries the hour, on the
 * project's clock (`fmtShortDay`, 24 h).
 *
 * FIX-FE-1 — `tz` is the served `project_timezone`: with it, both variants
 * carry the hour on that clock ("passes Mon 5 Oct, 09:00" / "time limit passed
 * Mon 5 Oct, 09:00"). Without it, as before.
 */
export const waitingForSignatureText = (w, tz) => {
  if (!w || !w.company_name) return '';
  const days = Number(w.days) || 0;
  const base = `Waiting for ${w.company_name} to sign — ${days} day${days === 1 ? '' : 's'}`;
  const limit = w.limit;
  if (!limit || !limit.days) return base;
  const served = onProjectClock(limit.passes_at, tz);
  if (limit.passed) {
    const on = served ? fmtShortDay(limit.passes_at, tz, { time: true }) : fshort(limit.passes_at);
    return `${base} · time limit passed${on ? ` ${on}` : ''}`;
  }
  const at = served ? fmtShortDay(limit.passes_at, tz, { time: true })
    : fmtShortDay(limit.passes_at, { time: true });
  return `${base} · time limit ${workingDaysText(limit.days)}${at ? `, passes ${at}` : ''}`;
};

/** H-FE-3 — the served `actions.why` once the receiving company's time to sign has passed. */
export const WHY_RECEIVER_LIMIT_PASSED = 'receiver_signature_limit_passed';

/** H-FE-3 — the outcome of a receiving attendee the completion marks, past the limit. */
export const DID_NOT_SIGN_LIMIT_PASSED = 'Did not sign — time limit passed';

/** H-FE-3 — the label of the reason the completion without the signature asks for. */
export const RECEIVER_REASON_LABEL = 'Why you are completing without their signature';

/**
 * H-FE-3 — the block at the top of the completion dialog when the time limit
 * has passed: "<Company> has not signed and the time limit has passed (5 working
 * days, on Mon 6 Oct). You can complete … and <Company> will be told."
 */
export const receiverLimitPassedText = (company, limit, tz) => {
  const co = company || 'The receiving company';
  const on = hoDay(limit?.passes_at, tz);
  const when = limit?.days
    ? ` (${workingDaysText(limit.days)}${on ? `, on ${on}` : ''})` : '';
  return `${co} has not signed and the time limit has passed${when}. `
    + 'You can complete this inspection without their signature. '
    + `The record will say they did not sign, and ${company || 'they'} will be told.`;
};

/**
 * H-FE-3 — a refusal of `POST /handover-inspections/{id}/complete`: the served
 * sentence (`detail.message`) when it comes; else, per code, these words.
 *
 * FIX-FE-1 — with the served `tz`, "it passes Mon 5 Oct, 09:00" (the hour, on
 * the project's clock); without it, the day as before.
 */
export const completeRefusalText = (err, company, tz) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message;
  }
  const co = company || 'the receiving company';
  const code = detail && typeof detail === 'object' ? detail.code : null;
  if (code === 'RECEIVER_LIMIT_NOT_PASSED') {
    const at = detail.limit?.passes_at;
    const on = onProjectClock(at, tz) ? fmtShortDay(at, tz, { time: true }) : fshort(at);
    return `The time limit for ${co} to sign has not passed yet${on ? ` — it passes ${on}` : ''}.`;
  }
  if (code === 'RECEIVER_ALREADY_SIGNED') return `${co} has signed. Complete the inspection the usual way.`;
  if (code === 'HANDOVER_REASON_REQUIRED') return 'Say why you are completing without their signature.';
  return serverMessage(err);
};

/** H-FE-3 — was this inspection completed without the receiving company's signature (`completion`)? */
export const receiverNotObtained = (hi) => hi?.completion?.receiver_signature === 'not_obtained';

/** H-FE-3 — the company that did not sign, from the record (else the inspection's receiver). */
const notSignedCompany = (hi) => hi?.completion?.receiver_not_signed?.project_company_name
  || hi?.handover_to_project_company_name || 'The receiving company';

/**
 * H-FE-3 — the record's line under the lifecycle: "Completed without <Company>'s
 * signature by <name> (<their company>) on <date> — the time limit of 5 working
 * days passed on Mon 6 Oct. Reason: "<reason>"." '' when not recorded.
 */
export const receiverNotSignedRecordText = (hi, byCompanyName) => {
  if (!receiverNotObtained(hi)) return '';
  const r = hi.completion.receiver_not_signed || {};
  const by = `${r.by_name || 'the organiser'}${byCompanyName ? ` (${byCompanyName})` : ''}`;
  const tz = hi.project_timezone;
  const on = hoDay(r.at, tz);
  const limit = r.limit || {};
  const passed = hoDay(limit.passes_at, tz);
  const lim = limit.days
    ? ` — the time limit of ${workingDaysText(limit.days)} passed${passed ? ` on ${passed}` : ''}` : '';
  return `Completed without ${notSignedCompany(hi)}'s signature by ${by}${on ? ` on ${on}` : ''}${lim}.`
    + `${r.reason ? ` Reason: "${r.reason}".` : ''}`;
};

/** H-FE-3 — an attendance line the completion marked "did not sign" because the time limit passed. */
export const didNotSignPastLimit = (a) => !!a?.did_not_sign?.receiver_signature_limit_passed;

/** H-FE-2 B4 — how a present line became present (`presence_source`). */
const PRESENCE_SOURCE = { signature: 'signed', organiser: 'recorded by the organiser' };

/**
 * DOSSIER-FE-1 B0 — the words of one attendance line, `{cls, text, sub}`: the
 * body of the inspection page's `AttendanceStatus`, moved verbatim, so the
 * dossier's Attendance column says what the inspection page says (one source).
 */
export function attendanceStatusView(a, tz) {
  let cls = 'text-slate-500';
  let text = 'Not signed yet';
  let sub = null;
  const source = PRESENCE_SOURCE[a.presence_source] || '';
  if (a.signed_at) {
    cls = 'text-emerald-400'; text = 'Signed'; sub = [hoStamp(a.signed_at, tz), source].filter(Boolean).join(' · ');
  } else if (a.state === 'awaiting_signature' && source) {
    sub = source;
  } else if (a.state === 'did_not_sign' && didNotSignPastLimit(a)) {
    // H-FE-3 (§19) — recorded by the completion, past the receiving company's time limit.
    cls = 'text-red-400'; text = DID_NOT_SIGN_LIMIT_PASSED; sub = a.did_not_sign?.reason || '';
  } else if (a.state === 'did_not_sign') {
    cls = 'text-red-400'; text = 'Attended, did not sign';
    sub = `${a.did_not_sign?.reason || ''} · recorded by ${a.did_not_sign?.by_name || 'the organiser'}`;
  } else if (a.state === 'not_attended') { text = 'Did not attend'; sub = 'recorded by the organiser'; } else if (a.kind === 'external') {
    cls = 'text-emerald-400'; text = 'Attended'; sub = 'external · recorded by the organiser';
  }
  return { cls, text, sub };
}

/**
 * H-FE-3 — the company of whoever completed without the signature.
 *
 * CLEAN-FE-1 — the record names it (`receiver_not_signed.by_project_company_name`,
 * QMP-BE-3). A record written before that carries only the id, so: the marked
 * lines' `by_project_company_name`, else the inspection's own parties by id,
 * else `userCompany(by)`; '' when none.
 */
export const receiverNotSignedByCompany = (hi, userCompany = () => '') => {
  const r = hi?.completion?.receiver_not_signed || {};
  if (typeof r.by_project_company_name === 'string' && r.by_project_company_name.trim()) {
    return r.by_project_company_name;
  }
  const marked = (hi?.attendees || []).find((a) => didNotSignPastLimit(a) && a.did_not_sign.by_project_company_name);
  if (marked) return marked.did_not_sign.by_project_company_name;
  const pc = r.by_project_company_id;
  if (pc && pc === hi?.handover_from_project_company_id) return hi.handover_from_project_company_name || '';
  if (pc && pc === hi?.handover_to_project_company_id) return hi.handover_to_project_company_name || '';
  return (r.by && userCompany(r.by)) || '';
};

/** H-FE-3 — the list's and the asset's status: "Completed · <Company> did not sign"; '' otherwise. */
export const completedWithoutReceiverText = (hi) => (receiverNotObtained(hi)
  ? `Completed · ${notSignedCompany(hi)} did not sign` : '');

/** The reason a present attendee blocks the completion, in words. */
export const blockerText = (b, hi) => {
  if (b?.code === 'awaiting_signature') return `${b.name || 'An attendee'} has not signed yet.`;
  if (b?.code === 'no_receiving_attendee_signed') return `Nobody from ${receivingCompanyName(hi)} has signed yet.`;
  if (b?.code === 'no_executing_party_attendee_signed') return `Nobody from ${receivingCompanyName(hi)} has signed yet.`;
  return b?.code || '';
};

/** "8 hours’ notice given" — `notice_given` is served in seconds. */
export const noticeGivenText = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return '';
  const h = Math.floor(Number(seconds) / 3600);
  return `${h} hours’ notice given`;
};

/**
 * CLEAN-FE-1 — the History entry of a completion without the receiving
 * company's signature: "Completed without <Company>'s signature — <name>
 * (<their company>) · Reason: "<reason>"". '' when the record has none.
 */
export const receiverNotSignedHistoryText = (hi, byCompanyName) => {
  const r = hi?.completion?.receiver_not_signed;
  if (!r) return '';
  const by = `${r.by_name || 'the organiser'}${byCompanyName ? ` (${byCompanyName})` : ''}`;
  return `Completed without ${notSignedCompany(hi)}'s signature — ${by}`
    + `${r.reason ? ` · Reason: "${r.reason}"` : ''}`;
};

/**
 * History of an inspection, from the facts it serves (newest first).
 * `byCompanyName` — the company of whoever completed without the receiving
 * company's signature, when the page resolved it (`receiverNotSignedByCompany`).
 */
export const hiHistory = (hi, row = {}, byCompanyName = receiverNotSignedByCompany(hi)) => {
  const out = [];
  const push = (at, what, who) => { if (at) out.push({ at, what, who: who || '' }); };
  const created = row.created_at || hi?.created_at;
  const createdBy = row.created_by_name || hi?.created_by_name;
  const at = scheduledInstant(hi);
  push(created, `Booked for ${at ? scheduledText(hi, hi?.project_timezone) : ''}${isQaCloseout(hi) ? ' (QA Closeout Inspection)'
    : (hi?.handover_to_project_company_name ? ` — ${hi.handover_to_project_company_name} invited` : '')}`, createdBy);
  (hi?.attendees || []).forEach((a) => {
    if (a.kind === 'external' && a.marked_at) push(a.marked_at, `Recorded external attendee ${a.name} (${a.company_name || ''})`, a.marked_by_name);
    else if (a.state === 'not_attended' && a.marked_at) push(a.marked_at, `Recorded that ${a.name} did not attend`, a.marked_by_name);
    if (a.signed_at) push(a.signed_at, `${a.name} signed attendance`, a.name);
    if (a.did_not_sign?.at) push(a.did_not_sign.at, `Recorded that ${a.name} attended and did not sign — “${a.did_not_sign.reason}”`, a.did_not_sign.by_name);
  });
  const req = row.completion_requested_at || hi?.completion?.requested_at;
  push(req, 'Asked to complete the inspection', row.completion_requested_by_name);
  const done = row.completed_at || hi?.completion?.completed_at;
  push(done, 'Completed', row.completed_by_name || 'ConstructQA');
  const notSigned = hi?.completion?.receiver_not_signed;
  if (notSigned) push(notSigned.at, receiverNotSignedHistoryText(hi, byCompanyName), notSigned.by_name);
  push(row.cancelled_at, 'Cancelled', row.cancelled_by_name);
  return out.sort((x, y) => String(y.at).localeCompare(String(x.at)));
};

// ---------------------------------------------------------------------------
// Assets: readiness and bookability from the served booking facts
// ---------------------------------------------------------------------------

/** "Not yet: 2 ITCs not complete, 1 open NCR" — the asset's own reasons. */
export const assetReadiness = (row) => {
  const out = [];
  const blockers = row?.booking?.blockers || [];
  if (blockers.some((b) => b.code === 'itcs')) {
    const n = Math.max(0, (row?.itc_total || 0) - (row?.itc_completed || 0));
    out.push(n ? `${n} ITC${n > 1 ? 's' : ''} not complete` : 'ITCs not complete');
  }
  blockers.filter((b) => b.code === 'ncr').forEach((b) => out.push(`${b.count} open NCR${b.count > 1 ? 's' : ''}`));
  blockers.filter((b) => b.code === 'rfi').forEach((b) => out.push(`${b.count} open RFI${b.count > 1 ? 's' : ''}`));
  if (blockers.some((b) => b.code === 'whole_package')) out.push('other assets of the package are not ready');
  if (blockers.some((b) => b.code === 'receiver_not_appointed')) out.push('the receiving company is not appointed in the published QMP');
  if (blockers.some((b) => b.code === 'executing_party_not_appointed')) out.push('the package has no Executing Party yet');
  if (blockers.some((b) => b.code === 'from_unresolved')) out.push('the handing-over company is not known');
  blockers.filter((b) => b.code === 'qmp_held').forEach((b) => out.push(
    `${b.company?.name || 'a company'} has not approved the QMP rules for this package yet`));
  return out;
};

export const handedOver = (row) => (row?.handover_completed || []).length > 0;

/** The live (scheduled) inspection an asset sits on, from the project's list. */
export const liveInspectionOf = (elementId, inspections = []) => (inspections || [])
  .find((i) => i?.status === 'scheduled' && (i.element_ids || []).includes(elementId)) || null;

/**
 * One asset's status in the booking table: `{ok, text, tone}` —
 * "Ready", "Not yet: …", "Handed over", "Already on HI-001", "QA Complete".
 */
export const assetBookingStatus = (row, inspections = []) => {
  if (row?.qa_complete) return { ok: false, text: 'QA Complete', tone: 'faint' };
  if (handedOver(row)) return { ok: false, text: 'Handed over', tone: 'faint' };
  const live = liveInspectionOf(row?.element_id, inspections);
  if (live) return { ok: false, text: `Already on ${live.document_id || 'an inspection'}`, tone: 'faint' };
  const r = assetReadiness(row);
  if (r.length) return { ok: false, text: `Not yet: ${r.join(', ')}`, tone: 'amber' };
  if (!row?.booking_gate_ok) return { ok: false, text: 'Not yet: QA gates not met', tone: 'amber' };
  return { ok: true, text: 'Ready', tone: 'ok' };
};

export const kindOfRow = (row) => (row?.booking?.kind === 'qa_closeout' ? 'qa_closeout' : 'handover');

// ---------------------------------------------------------------------------
// Errors: the server's own sentence
// ---------------------------------------------------------------------------

/** `detail.message` first (every 409 of these doors carries one), then a string detail, then the fallback. */
export const serverMessage = (err, fallback = 'This could not be recorded.') => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message;
  }
  if (detail && typeof detail === 'object' && (detail.code || detail.error)) return String(detail.code || detail.error);
  return fallback;
};
